/**
 * Homestead structural support — pure CONNECTIVITY, not load physics (the spec's
 * non-goal stands: nothing collapses under weight). The rule: a piece is valid
 * only if it is GROUNDED (its origin rests on the terrain) or WELDED — sharing a
 * coincident snap socket — with a piece that is transitively supported. Demolish
 * cascades: removing a piece also removes every piece that would float.
 *
 * Pure + pmath-safe (arithmetic, Math.sqrt/floor only; compileAsset is already
 * pmath-routed). Shared so the SERVER enforces and the client mirrors for the
 * ghost-validity tint + dependent highlighting.
 */
import { compileAsset } from "../proceduralAssetCompile.js";
import type { PlanetSampler } from "../universe.js";
import { surfaceSupportRadius } from '../surface.js';
import { pieceWorldObbs, standRadiusObbs, type WorldObb } from "./collision.js";
import { PIECE_CATALOG } from "./pieces.js";
import type { Piece, PlacedPiece, Quat, Vec3 } from "./types.js";

/** Two sockets closer than this (metres) are welded. Snapped placements
 *  coincide to float precision; the slack absorbs wire round-trips. */
export const WELD_TOL_M = 0.25;
/** A piece origin within this height of the terrain surface is grounded. */
export const GROUND_EPS_M = 0.75;
/** FURNITURE rule (2026-08-02, settlement-loop P0): a furniture piece is
 *  supported when its origin rests within this of a supported structural
 *  piece's walkable top (or the terrain). Furniture is socketless — never a
 *  weld-graph node, never provides support, cascades with its floor. */
export const REST_EPS_M = 0.4;
/** Rest-check prefilter: structural pieces whose origin is farther than this
 *  from the furniture origin cannot be under it (largest piece span + slack). */
const REST_REACH_M = 6;

/** Catalog group check — the support rules branch on it. */
export function isFurniturePiece(t: number): boolean {
  return PIECE_CATALOG[t]?.group === "furniture";
}

/**
 * Does a furniture piece rest on any `allowed` structural piece's walkable
 * top? Uses the shared OBB stand probe at the furniture ORIGIN: the highest
 * walkable face under it must sit within REST_EPS of the origin radius.
 */
function restsOn(f: Piece, structural: readonly PlacedPiece[],
  allowed: (pid: number) => boolean): boolean {
  const [x, y, z] = f.p;
  const obbs: WorldObb[] = [];
  const reach2 = REST_REACH_M * REST_REACH_M;
  for (const s of structural) {
    if (!allowed(s.pid)) continue;
    const dx = s.p[0] - x, dy = s.p[1] - y, dz = s.p[2] - z;
    if (dx * dx + dy * dy + dz * dz > reach2) continue;
    obbs.push(...pieceWorldObbs(s));
  }
  if (obbs.length === 0) return false;
  const stand = standRadiusObbs(obbs, x, y, z);
  if (stand === null) return false;
  const len = Math.sqrt(x * x + y * y + z * z) || 1;
  return Math.abs(len - stand) <= REST_EPS_M;
}

// ── catalog socket cache (doc-space positions per catalog index) ─────────────

let socketCache: Vec3[][] | null = null;
function catalogSockets(): Vec3[][] {
  if (!socketCache) {
    socketCache = PIECE_CATALOG.map((e) =>
      compileAsset(e.doc).sockets.map((s) => [s.position[0], s.position[1], s.position[2]] as Vec3));
  }
  return socketCache;
}

/** v rotated by q plus offset o (plain arithmetic). */
function rot(v: Vec3, q: Quat, o: Vec3): Vec3 {
  const [qx, qy, qz, qw] = q;
  const tx = 2 * (qy * v[2] - qz * v[1]);
  const ty = 2 * (qz * v[0] - qx * v[2]);
  const tz = 2 * (qx * v[1] - qy * v[0]);
  return [
    v[0] + qw * tx + qy * tz - qz * ty + o[0],
    v[1] + qw * ty + qz * tx - qx * tz + o[1],
    v[2] + qw * tz + qx * ty - qy * tx + o[2],
  ];
}

/** Planet-local socket positions of a piece (empty for unknown catalog ids). */
export function pieceSocketsWorld(p: Piece): Vec3[] {
  const local = catalogSockets()[p.t];
  if (!local) return [];
  return local.map((s) => rot(s, p.q, p.p));
}

/** Is the piece resting on the terrain? Origin height above the surface ≤ eps. */
export function groundedOn(sampler: PlanetSampler, p: Piece, eps = GROUND_EPS_M): boolean {
  const [x, y, z] = p.p;
  const len = Math.sqrt(x * x + y * y + z * z) || 1;
  const surface = surfaceSupportRadius(sampler, x / len, y / len, z / len);
  return len - surface <= eps;
}

// ── weld adjacency via a socket spatial hash (O(n·s), not pairwise) ──────────

const CELL = 0.5; // ≥ 2·WELD_TOL so a neighbour scan of 27 cells never misses
function cellKey(x: number, y: number, z: number): string {
  return `${Math.floor(x / CELL)},${Math.floor(y / CELL)},${Math.floor(z / CELL)}`;
}

interface Graph {
  /** pid → welded neighbour pids. */
  adj: Map<number, Set<number>>;
  grounded: Set<number>;
}

function buildGraph(pieces: readonly PlacedPiece[], isGrounded: (p: PlacedPiece) => boolean): Graph {
  const adj = new Map<number, Set<number>>();
  const grounded = new Set<number>();
  // Socket hash: cell → [pid, x, y, z] entries.
  const hash = new Map<string, [number, number, number, number][]>();
  const tol2 = WELD_TOL_M * WELD_TOL_M;
  for (const piece of pieces) {
    adj.set(piece.pid, new Set());
    if (isGrounded(piece)) grounded.add(piece.pid);
    for (const s of pieceSocketsWorld(piece)) {
      const key = cellKey(s[0], s[1], s[2]);
      const list = hash.get(key);
      if (list) list.push([piece.pid, s[0], s[1], s[2]]);
      else hash.set(key, [[piece.pid, s[0], s[1], s[2]]]);
    }
  }
  // Weld: sockets of DIFFERENT pieces within tolerance (scan own + 26 neighbours).
  for (const piece of pieces) {
    const mine = adj.get(piece.pid)!;
    for (const s of pieceSocketsWorld(piece)) {
      const cx = Math.floor(s[0] / CELL), cy = Math.floor(s[1] / CELL), cz = Math.floor(s[2] / CELL);
      for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) for (let dz = -1; dz <= 1; dz++) {
        const list = hash.get(`${cx + dx},${cy + dy},${cz + dz}`);
        if (!list) continue;
        for (const [pid, x, y, z] of list) {
          if (pid === piece.pid || mine.has(pid)) continue;
          const ex = x - s[0], ey = y - s[1], ez = z - s[2];
          if (ex * ex + ey * ey + ez * ez <= tol2) {
            mine.add(pid);
            adj.get(pid)?.add(piece.pid);
          }
        }
      }
    }
  }
  return { adj, grounded };
}

/**
 * Socket-connected structural components. Furniture is deliberately omitted:
 * it rests on a structure but must neither join two buildings nor enlarge the
 * room solver's structural bounds. Components and their members are returned
 * in ascending pid order so every peer rasterizes them in the same order.
 */
export function structuralComponents(pieces: readonly PlacedPiece[]): PlacedPiece[][] {
  const structural = pieces
    .filter((p) => !isFurniturePiece(p.t))
    .slice()
    .sort((a, b) => a.pid - b.pid);
  const graph = buildGraph(structural, () => false);
  const byPid = new Map(structural.map((p) => [p.pid, p]));
  const seen = new Set<number>();
  const out: PlacedPiece[][] = [];
  for (const root of structural) {
    if (seen.has(root.pid)) continue;
    const queue = [root.pid];
    const component: PlacedPiece[] = [];
    seen.add(root.pid);
    while (queue.length > 0) {
      const pid = queue.pop()!;
      const piece = byPid.get(pid);
      if (piece) component.push(piece);
      const neighbours = [...(graph.adj.get(pid) ?? [])].sort((a, b) => b - a);
      for (const next of neighbours) {
        if (seen.has(next)) continue;
        seen.add(next);
        queue.push(next);
      }
    }
    component.sort((a, b) => a.pid - b.pid);
    out.push(component);
  }
  return out;
}

/** BFS from the grounded roots, optionally treating `without` as removed. */
function reach(g: Graph, without: number | null): Set<number> {
  const seen = new Set<number>();
  const queue: number[] = [];
  for (const pid of g.grounded) {
    if (pid === without) continue;
    seen.add(pid);
    queue.push(pid);
  }
  while (queue.length > 0) {
    const pid = queue.pop()!;
    for (const next of g.adj.get(pid) ?? []) {
      if (next === without || seen.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
  }
  return seen;
}

/** The pids currently supported (reachable from grounded pieces). Furniture is
 *  NOT a graph node: it is supported when grounded or resting on a supported
 *  structural piece, and it never supports anything else. */
export function supportedSet(pieces: readonly PlacedPiece[],
  isGrounded: (p: PlacedPiece) => boolean): Set<number> {
  const structural = pieces.filter((p) => !isFurniturePiece(p.t));
  const seen = reach(buildGraph(structural, isGrounded), null);
  for (const p of pieces) {
    if (!isFurniturePiece(p.t)) continue;
    if (isGrounded(p) || restsOn(p, structural, (pid) => seen.has(pid))) seen.add(p.pid);
  }
  return seen;
}

/**
 * The pieces that would FLOAT if `pid` were removed: supported now, unsupported
 * without it. Legacy floaters (never supported) are not blamed on this piece.
 * This is both the demolish cascade set and the aim-highlight set. Furniture
 * joins the cascade when the floor it rests on goes; removing furniture itself
 * never cascades anything.
 */
export function wouldFloat(pieces: readonly PlacedPiece[], pid: number,
  isGrounded: (p: PlacedPiece) => boolean): number[] {
  const removedPiece = pieces.find((p) => p.pid === pid);
  const structural = pieces.filter((p) => !isFurniturePiece(p.t));
  const g = buildGraph(structural, isGrounded);
  const before = reach(g, null);
  const removingFurniture = removedPiece !== undefined && isFurniturePiece(removedPiece.t);
  const after = removingFurniture ? before : reach(g, pid);
  const out: number[] = [];
  for (const q of before) if (q !== pid && !after.has(q)) out.push(q);
  // Furniture cascade: resting support lost once the removal set is gone.
  const removed = new Set<number>([pid, ...out]);
  const survivors = structural.filter((s) => !removed.has(s.pid));
  for (const p of pieces) {
    if (!isFurniturePiece(p.t) || p.pid === pid || isGrounded(p)) continue;
    if (!restsOn(p, structural, (q) => before.has(q))) continue; // legacy floater
    if (!restsOn(p, survivors, (q) => after.has(q) && !removed.has(q))) out.push(p.pid);
  }
  return out;
}

/**
 * May this NEW piece be placed? Structural: grounded, or welded
 * (socket-coincident) to an already-SUPPORTED piece. Furniture: grounded, or
 * resting on a supported structural piece's walkable top. `supported` =
 * supportedSet(existing) — pass it in so a ghost re-check each frame doesn't
 * rebuild the graph.
 */
export function placementSupported(piece: Piece, existing: readonly PlacedPiece[],
  supported: ReadonlySet<number>, isGroundedNew: (p: Piece) => boolean): boolean {
  if (isGroundedNew(piece)) return true;
  if (isFurniturePiece(piece.t)) {
    const structural = existing.filter((p) => !isFurniturePiece(p.t));
    return restsOn(piece, structural, (pid) => supported.has(pid));
  }
  const mine = pieceSocketsWorld(piece);
  if (mine.length === 0) return false;
  const tol2 = WELD_TOL_M * WELD_TOL_M;
  for (const other of existing) {
    if (!supported.has(other.pid)) continue;
    for (const os of pieceSocketsWorld(other)) {
      for (const s of mine) {
        const dx = os[0] - s[0], dy = os[1] - s[1], dz = os[2] - s[2];
        if (dx * dx + dy * dy + dz * dz <= tol2) return true;
      }
    }
  }
  return false;
}
